import { IDataAnalyticsResponse } from '../definitions/AIQuery'
import { IApprovalGroupFilters, IApprovalRequest } from '../definitions/Approval'
import { IClaimFilter, IClaimKPI, IClaimLine, IClaimsBreakdown } from '../definitions/Claims'
import { IPagination, IPaginationQueryResponse, ISystemConfigsCollection } from '../definitions/Common'
import { IContract, IContractDetails, IContractsStatusAnalytics, ISimpleContractWithApproval } from '../definitions/Contract'
import { IContractSchema, IContractSchemaWithApproval } from '../definitions/ContractSchema'
import { IChartData, ICostUtilization, IKpiResponse, IMarginExpense, IRevenueExpense } from '../definitions/DataAnalytics'
import { IContractEditorOptions, IWorkflowEditorOptions } from '../definitions/Filter'
import { IFundsBreakdown } from '../definitions/Funds'
import { IMedia } from '../definitions/Media'
import { IAttributedMember, IAttributedMembersFilter, IAttributedMembersKPI, IMember, IMemberDetailsResponse, IMembersFilter, IMembersKPI } from '../definitions/Members'
import { IMaterializedViewRefreshResponse } from '../definitions/nexus/QueryStudioDefinitions'
import { IFlow } from '../definitions/Nodes'
import { IOverviewData } from '../definitions/Overview'
import { IUserRole } from '../definitions/Permissions'
import { IProgramDetails } from '../definitions/Program'
import { ICounterparty, IProviderDetails, IProviderFilter, IProviderKPI, IProviderPerformanceBreakdown, IProvidersKPI } from '../definitions/Providers'
import { IQualityMeasureFilter, IQualityMeasureKPI, IQualityMeasuresBreakdown } from '../definitions/QualityMeasures'
import { ICompiledReport, ICompiledReportInfo, IPreviewURI, IReport, IReportPlaceholder, ReportPageOrientation, ReportPageSize } from '../definitions/report_builder/ReportBuilderDefinitions'
import { IProviderExpense, ITosExpense } from '../definitions/ServiceExpense'
import { ISettlementSchema, ISettlementSchemaWithApprovals } from '../definitions/SettlementSchema'
import { ITimeSettings } from '../definitions/Shared'
import { ITCOCSummary, ITCOCSummaryFilter } from '../definitions/Summary'
import { ITooltipData } from '../definitions/Tooltip'
import { ISignedURL, IUploadedFile } from '../definitions/Upload'
import { IUser } from '../definitions/Users'
import { IWorkFlow } from '../definitions/Workflow'
import ApiUrlCompiler from './api_urls/ApiUrlCompiler'
import API_URLS from './ApiUrls'
import HttpClient from './HttpClient'
import AuthApiClient from './sub_api_clients/AuthenticationApiClient'
import ContractIngestionApiClient from './sub_api_clients/ContractIngestionApiClient'
import FiltersApiClient from './sub_api_clients/FiltersApiClient'
import FlowApiClient from './sub_api_clients/FlowApiClient'

class ApiClientClass {
    Filters = FiltersApiClient
    Auth = AuthApiClient
    Flow = FlowApiClient
    ContractIngestion = ContractIngestionApiClient

    getAllContracts = async (include?: ('counterparty.providers')[]): Promise<IContract[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getAllContracts)
        const body = {
            include
        }

        const res = await HttpClient.post<IContract[]>(url, body, headers)
        return res
    }

    getContracts = async (pagination: IPagination): Promise<IPaginationQueryResponse<IContract>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContracts)
        const body = { pagination }
        const res = await HttpClient.post<IPaginationQueryResponse<IContract>>(url, body, headers)
        return res
    }

    updateContract = async (contract: IContract): Promise<IContract> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.updateContract)
        const body = {
            contract
        }

        return await HttpClient.post<IContract>(url, body, headers)
    }

    deleteContract = async (contract: IContract): Promise<boolean> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.deleteContract)
        const body = {
            contract
        }

        return await HttpClient.post<boolean>(url, body, headers)
    }

    deleteContractFiles = async (contractUUID: string): Promise<boolean> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.deleteContractFiles)
        const body = {
            contractUUID
        }

        return await HttpClient.post<boolean>(url, body, headers)
    }

    getContractDetails = async (contractId: number, time: ITimeSettings): Promise<IContractDetails> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractDetails)
        const body = {
            contractId,
            time
        }

        return await HttpClient.post<IContractDetails>(url, body, headers)
    }

    getContractsStatusAnalytics = async (time: ITimeSettings): Promise<IContractsStatusAnalytics> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractsStatusAnalytics)
        const body = { time }

        return await HttpClient.post<IContractsStatusAnalytics>(url, body, headers)
    }

    getContractFunds = async (contractId: number, time: ITimeSettings): Promise<IFundsBreakdown> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractFunds)
        const body = {
            contractId,
            time
        }

        return await HttpClient.post<IFundsBreakdown>(url, body, headers)
    }

    getContractProviders = async (contractId: number, time: ITimeSettings): Promise<IProviderPerformanceBreakdown> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractProviders)
        const body = {
            contractId,
            time
        }

        return await HttpClient.post<IProviderPerformanceBreakdown>(url, body, headers)
    }

    getContractQualityMeasures = async (contractId: number, time: ITimeSettings): Promise<IQualityMeasuresBreakdown> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractQualityMeasures)
        const body = {
            contractId,
            time
        }

        return await HttpClient.post<IQualityMeasuresBreakdown>(url, body, headers)
    }

    getContractClaims = async (contractId: number, time: ITimeSettings, pagination: IPagination, filter: IClaimFilter): Promise<IClaimsBreakdown> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractClaims)
        const body = {
            contractId,
            time,
            pagination,
            filter
        }

        return await HttpClient.post<IClaimsBreakdown>(url, body, headers)
    }

    getContractInfo = async (contractId: number): Promise<IContract> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractInfo)
        const body = {
            contractId
        }

        return await HttpClient.post<IContract>(url, body, headers)
    }

    getContract = async (contractUUID: string): Promise<IContract> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContract)
        const body = {
            contractUUID
        }

        return await HttpClient.post<IContract>(url, body, headers)
    }

    createNewContract = async (schemaForeignKey: string, externalId: string, contractName: string): Promise<IContract> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.createNewContract)
        const body = { schemaForeignKey, externalId, contractName }

        const res = await HttpClient.post<IContract>(url, body, headers)
        return res
    }

    getContractEditorOptions = async (): Promise<IContractEditorOptions> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractEditorOptions)
        const body = {
        }

        const res = await HttpClient.post<IContractEditorOptions>(url, body, headers)
        return res
    }

    createNewContractFile = async (contractUUID: string, fileName: string, fileSize: number, key: string): Promise<IUploadedFile> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.createNewContractFile)
        const body = {
            contractUUID,
            fileName,
            fileSize,
            key
        }

        return await HttpClient.post<IUploadedFile>(url, body, headers)
    }

    setContractFile = async (contractFile: IUploadedFile): Promise<IUploadedFile> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.setContractFile)
        const body = {
            contractFile
        }

        return await HttpClient.post<IUploadedFile>(url, body, headers)
    }

    getAllCounterparties = async (): Promise<ICounterparty[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getAllCounterparties)
        const body = {}

        const res = await HttpClient.post<ICounterparty[]>(url, body, headers)
        return res
    }

    getQualityMeasuresKPI = async (contractId: number, time: ITimeSettings, filter: IQualityMeasureFilter): Promise<IQualityMeasureKPI> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getProvidersKPI)
        const body = {
            contractId,
            time,
            filter
        }

        const res = await HttpClient.post<IQualityMeasureKPI>(url, body, headers)
        return res
    }

    getProvidersKPI = async (contractId: number, time: ITimeSettings, filter: IProviderFilter): Promise<IProvidersKPI> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getProvidersKPI)
        const body = {
            contractId,
            time,
            filter
        }

        const res = await HttpClient.post<IProvidersKPI>(url, body, headers)
        return res
    }

    getMembersKPI = async (time: ITimeSettings, filter: IClaimFilter): Promise<IMembersKPI> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getMembersKPI)
        const body = {
            time,
            filter
        }

        return await HttpClient.post<IMembersKPI>(url, body, headers)
    }

    getClaimsKPI = async (contractId: number, time: ITimeSettings, filter: IClaimFilter): Promise<IClaimKPI> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getClaimsKPI)
        const body = {
            contractId,
            time,
            filter
        }

        return await HttpClient.post<IClaimKPI>(url, body, headers)
    }

    getClaimLines = async (claimId: number, contractId?: number): Promise<IClaimLine[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getClaimLines)
        const body = {
            claimId,
            contractId
        }

        return await HttpClient.post<IClaimLine[]>(url, body, headers)
    }

    getCounterpartyDetails = async (counterpartyId: number): Promise<ICounterparty> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getCounterpartyDetails)
        const body = {
            counterpartyId
        }

        return await HttpClient.post<ICounterparty>(url, body, headers)
    }

    getProviderDetails = async (providerId: number, contractId: number, time: ITimeSettings): Promise<IProviderDetails> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getProviderDetails)
        const body = {
            providerId,
            contractId,
            time
        }

        return await HttpClient.post<IProviderDetails>(url, body, headers)
    }

    getOverviewData = async (): Promise<IOverviewData> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getOverviewData)
        const body = {}

        return await HttpClient.post<IOverviewData>(url, body, headers)
    }

    getMembers = async (time: ITimeSettings, pagination: IPagination, filter: IMembersFilter): Promise<IMember[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getMembers)
        const body = {
            time,
            pagination,
            filter
        }

        return await HttpClient.post<IMember[]>(url, body, headers)
    }

    getAttributedMembers = async (contractId: number, time: ITimeSettings, pagination: IPagination, filter: IMembersFilter): Promise<IAttributedMember[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getAttributedMembers)
        const body = {
            time,
            contractId,
            pagination,
            filter
        }

        return await HttpClient.post<IAttributedMember[]>(url, body, headers)
    }

    getAttributedMembersKPI = async (contractId: number, time: ITimeSettings, filter: IAttributedMembersFilter): Promise<IAttributedMembersKPI> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getAttributedMembersKPI)
        const body = {
            time,
            contractId,
            filter
        }

        return await HttpClient.post<IAttributedMembersKPI>(url, body, headers)
    }

    aiDataAnalyst = async (userQuery: string): Promise<IDataAnalyticsResponse> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.aiDataAnalyst)
        const body = {
            userQuery
        }

        return await HttpClient.post<IDataAnalyticsResponse>(url, body, headers)
    }

    readUploadedFiles = async (keys: string[]): Promise<ISignedURL[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.readUploadedFiles)
        const body = {
            keys
        }

        return await HttpClient.post<ISignedURL[]>(url, body, headers)
    }

    getSignedURLs = async (numberItemsToUpload: number): Promise<ISignedURL[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getSignedURLs)
        const body = {
            numberItemsToUpload
        }

        return await HttpClient.post<ISignedURL[]>(url, body, headers)
    }

    getAllFiles = async (): Promise<IUploadedFile[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getAllFiles)
        const body = {}
        console.log('DOING GET ALL FILES')

        return await HttpClient.post<IUploadedFile[]>(url, body, headers)
    }

    getProgramDetails = async (contractId: number, time: ITimeSettings): Promise<IProgramDetails> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getProgramDetails)
        const body = {
            contractId,
            time
        }

        return await HttpClient.post<IProgramDetails>(url, body, headers)
    }

    getTCOCSummary = async (time: ITimeSettings, filter: ITCOCSummaryFilter): Promise<ITCOCSummary> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getTCOCSummary)
        const body = {
            time,
            filter
        }

        return await HttpClient.post<ITCOCSummary>(url, body, headers)
    }

    getProviderKPI = async (providerId: number, time: ITimeSettings): Promise<IProviderKPI> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getProviderKPI)
        const body = {
            providerId,
            time
        }

        const res = await HttpClient.post<IProviderKPI>(url, body, headers)
        return res
    }

    getMemberDetails = async (memberId: number, time: ITimeSettings): Promise<IMemberDetailsResponse> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getMemberDetails)
        const body = {
            memberId,
            time
        }

        const res = await HttpClient.post<IMemberDetailsResponse>(url, body, headers)
        return res
    }

    getMemberDetailsWithPermission = async (memberId: number, time: ITimeSettings): Promise<IMemberDetailsResponse> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getMemberDetailsWithPermission)
        const body = {
            memberId,
            time
        }

        const res = await HttpClient.post<IMemberDetailsResponse>(url, body, headers)
        return res
    }

    getAllWorkflows = async (pagination: IPagination): Promise<IPaginationQueryResponse<IWorkFlow>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getAllWorkflows)
        const body = { pagination }

        return await HttpClient.post<IPaginationQueryResponse<IWorkFlow>>(url, body, headers)
    }

    getWorkflow = async (uuid: string): Promise<IWorkFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getWorkflow)
        const body = { uuid }
        return await HttpClient.post<IWorkFlow>(url, body, headers)
    }

    updateWorkflow = async (workflow: IWorkFlow): Promise<IWorkFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.updateWorkflow)
        const body = { workflow }

        return await HttpClient.post<IWorkFlow>(url, body, headers)
    }

    createWorkflow = async (workflow: IWorkFlow): Promise<IWorkFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.createWorkflow)
        const body = { workflow }
        return await HttpClient.post<IWorkFlow>(url, body, headers)
    }

    deleteWorkflow = async (workflowUUID: string): Promise<IWorkFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.deleteWorkflow)
        const body = { workflowUUID }

        return await HttpClient.post<IWorkFlow>(url, body, headers)
    }

    getWorkflowEditorOptions = async (): Promise<IWorkflowEditorOptions> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getWorkflowEditorOptions)
        const body = {}

        return await HttpClient.post<IWorkflowEditorOptions>(url, body, headers)
    }

    getSettlements = async (pagination: IPagination, contractId: number): Promise<IPaginationQueryResponse<ISettlementSchemaWithApprovals>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getSettlements)
        const body = {
            pagination,
            contractId
        }

        const res = await HttpClient.post<IPaginationQueryResponse<ISettlementSchemaWithApprovals>>(url, body, headers)
        return res
    }

    getSettlementDetails = async (settlementId?: string): Promise<{ settlement: ISettlementSchema & { userName: string, contractName: string } }> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getSettlementDetails)
        const body = {
            settlementId
        }

        const res = await HttpClient.post<{ settlement: ISettlementSchema & { userName: string, contractName: string } }>(url, body, headers)
        return res
    }

    getContractSettlementFlow = async (contractId: number): Promise<IFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getContractSettlementFlow)
        const body = {
            contractId
        }

        const res = await HttpClient.post<IFlow>(url, body, headers)
        return res
    }

    getGroup = async (groupUuid: string): Promise<IUserRole> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getGroup)
        const body = { groupUuid }

        const res = await HttpClient.post<IUserRole>(url, body, headers)
        return res
    }

    createGroup = async (name: string, description: string, isDefault: boolean, allow: string[], disallow: string[]): Promise<IUserRole[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.createGroup)
        const body = { name, description, isDefault, allow, disallow }

        return await HttpClient.post<IUserRole[]>(url, body, headers)
    }

    getAllUserRoles = async (): Promise<IUserRole[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getAllUserRoles)
        const body = {}

        return await HttpClient.post<IUserRole[]>(url, body, headers)
    }

    getApprovalGroups = async (filters?: IApprovalGroupFilters[]): Promise<IUserRole[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getApprovalGroups)
        const body = { filters }

        return await HttpClient.post<IUserRole[]>(url, body, headers)
    }

    updateGroup = async (group: IUserRole): Promise<IUserRole[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.updateGroup)
        const body = { group }

        return await HttpClient.post<IUserRole[]>(url, body, headers)
    }

    deleteGroup = async (groupUuid: string): Promise<IUserRole[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.deleteGroup)
        const body = { groupUuid }

        return await HttpClient.post<IUserRole[]>(url, body, headers)
    }

    getUserByRole = async (roleKey: IUserRole['key']): Promise<IUser[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getUserByRole)
        const body = { roleKey }
        return await HttpClient.post<IUser[]>(url, body, headers)
    }

    getUser = async (userUUID: string): Promise<IUser> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getUser)
        const body = { userUUID }
        return await HttpClient.post<IUser>(url, body, headers)
    }

    getDynamoUser = async (name: string, userName: string): Promise<IUser> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getDynamoUser)
        const body = { name, userName }

        console.log('URL', url)
        console.log('headers', headers)
        console.log('body', body)

        return await HttpClient.post<IUser>(url, body, headers)
    }

    getAllUsers = async (): Promise<IUser[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getAllUsers)
        const body = {}
        return await HttpClient.post<IUser[]>(url, body, headers)
    }

    createTooltip = async (tooltip: ITooltipData): Promise<ITooltipData> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.createTooltip)
        const body = { tooltip }
        return await HttpClient.post<ITooltipData>(url, body, headers)
    }

    getTooltip = async (uuid: string): Promise<ITooltipData> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getTooltip)
        const body = { uuid }
        return await HttpClient.post<ITooltipData>(url, body, headers)
    }

    getTooltipByKey = async (tooltipKey: string): Promise<ITooltipData> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getTooltipByKey)
        const body = { tooltipKey }
        return await HttpClient.post<ITooltipData>(url, body, headers)
    }

    getAllTooltips = async (): Promise<ITooltipData[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getAllTooltips)
        const body = {}

        return await HttpClient.post<ITooltipData[]>(url, body, headers)
    }

    updateTooltip = async (tooltip: ITooltipData): Promise<ITooltipData> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.updateTooltip)
        const body = { tooltip }
        return await HttpClient.post<ITooltipData>(url, body, headers)
    }

    deleteTooltip = async (uuid: string): Promise<boolean> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.deleteTooltip)
        const body = { uuid }
        return await HttpClient.post<boolean>(url, body, headers)
    }

    createContractSchema = async (contractSchemaName: IContractSchema['name']) => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.createContractSchema)
        const body = { contractSchemaName }
        return await HttpClient.post<IContractSchema>(url, body, headers)
    }

    cloneContractSchema = async (contractSchemaUUID: IContractSchema['uuid']) => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.cloneContractSchema)
        const body = { contractSchemaUUID }
        return await HttpClient.post<IContractSchema>(url, body, headers)
    }

    getContractSchema = async (uuid: string): Promise<IContractSchema> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractSchema)
        const body = { uuid }
        return await HttpClient.post<IContractSchema>(url, body, headers)
    }

    getContractSchemaByKey = async (foreignKey: string): Promise<IContractSchema> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractSchemaByKey)
        const body = { foreignKey }
        return await HttpClient.post<IContractSchema>(url, body, headers)
    }

    getAllContractSchemas = async (): Promise<IContractSchema[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getAllContractSchemas)
        const body = {}
        return await HttpClient.post<IContractSchema[]>(url, body, headers)
    }

    getContractSchemas = async (pagination: IPagination): Promise<IContractSchema[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractSchemas)
        const body = { pagination }
        return await HttpClient.post<IContractSchema[]>(url, body, headers)
    }

    updateContractSchema = async (contractSchema: IContractSchema): Promise<IContractSchema> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.updateContractSchema)
        const body = { contractSchema }
        return await HttpClient.post<IContractSchema>(url, body, headers)
    }

    deleteContractSchema = async (uuid: string): Promise<boolean> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.deleteContractSchema)
        const body = { uuid }
        return await HttpClient.post<boolean>(url, body, headers)
    }

    getContractSchemaVersions = async (contractSchemaAncestorKey?: string): Promise<IContractSchemaWithApproval[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractSchemaVersions)
        const body = { contractSchemaAncestorKey }
        const res = await HttpClient.post<IContractSchemaWithApproval[]>(url, body, headers)
        return res
    }

    addContractSchemaVersion = async (schemaUUID: IContractSchema['uuid']): Promise<IContractSchema> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.addContractSchemaVersion)
        const body = { schemaUUID }
        return await HttpClient.post<IContractSchema>(url, body, headers)
    }

    getSchemaNamyByUUID = async (uuid: string): Promise<string> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getSchemaNameByUUID)
        const body = { uuid }
        return await HttpClient.post<string>(url, body, headers)
    }

    createPDF = async (settlementData: ISettlementSchema, contract: IContract): Promise<Blob> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.createPDF)
        const body = { settlementData, contract }
        const blobRes = HttpClient.postBlob(url, body, headers)

        return blobRes
    }

    generatePreview = async (report: IReport): Promise<IPreviewURI> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.generatePreview)
        const body = { report }
        return await HttpClient.post<IPreviewURI>(url, body, headers)
    }

    getContractReports = async (contractId: number): Promise<Record<string, ICompiledReportInfo[]>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractReports)
        const body = { contractId }
        return await HttpClient.post<Record<string, ICompiledReportInfo[]>>(url, body, headers)
    }

    getReportsByTemplate = async (templateUUID: string): Promise<Array<ICompiledReport & { username: string }>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getReportsByTemplate)
        const body = { templateUUID }

        return await HttpClient.post<Array<ICompiledReport & { username: string }>>(url, body, headers)
    }

    getReports = async (): Promise<Array<IReport & { username: string }>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getReports)
        const body = {}
        return await HttpClient.post<Array<IReport & { username: string }>>(url, body, headers)
    }

    createReport = async (name?: string, pageSize?: ReportPageSize, pageOrientation?: ReportPageOrientation): Promise<IReport> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.createReport)
        const body = { name, pageSize, pageOrientation }
        return await HttpClient.post<IReport>(url, body, headers)
    }

    updateReport = async (report: IReport): Promise<IReport> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.updateReport)
        const body = { report }
        return await HttpClient.post<IReport>(url, body, headers)
    }

    cloneReport = async (report: IReport): Promise<IReport> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.cloneReport)
        const body = { report }
        return await HttpClient.post<IReport>(url, body, headers)
    }

    deleteReport = async (uuid: string): Promise<boolean> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.deleteReport)
        const body = { uuid }
        return await HttpClient.post<boolean>(url, body, headers)
    }

    generateReportPlaceholders = async (uuid: string): Promise<IReportPlaceholder[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.generateReportPlaceholders)
        const body = { uuid }
        return await HttpClient.post<IReportPlaceholder[]>(url, body, headers)
    }

    getPdfPresignedUrl = async (fileKey: string, bucketName?: string): Promise<IPreviewURI> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getPdfPresignedUrl)
        const body = { fileKey, bucketName }
        return await HttpClient.post<IPreviewURI>(url, body, headers)
    }

    getUploadSignedURL = async (fileName: string, extension: string, bucketName: string, contentType: string): Promise<ISignedURL> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getUploadSignedURL)
        const body = { fileName, extension, bucketName, contentType }

        return await HttpClient.post<ISignedURL>(url, body, headers)
    }

    createMedia = async (fileName: string, size: number, description: string, dimensions: string): Promise<{ media: IMedia, signedUrl: string }> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.createMedia)
        const body = { fileName, size, description, dimensions }

        return await HttpClient.post<{ media: IMedia, signedUrl: string }>(url, body, headers)
    }

    updateMedia = async (newMedia: IMedia): Promise<IMedia> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.updateMedia)
        const body = { newMedia }

        return await HttpClient.post<IMedia>(url, body, headers)
    }

    readMediaItem = async (key: string, bucket: string): Promise<IPreviewURI> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.readMediaItem)
        const body = { key, bucket }

        return await HttpClient.post<IPreviewURI>(url, body, headers)
    }

    getAllMedia = async (): Promise<IMedia[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getAllMedia)
        const body = {}

        return await HttpClient.post<IMedia[]>(url, body, headers)
    }

    deleteMediaFile = async (fileKey: string, bucketName: string, uuid: string): Promise<boolean> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.deleteMediaFile)
        const body = { fileKey, bucketName, uuid }

        return await HttpClient.post<boolean>(url, body, headers)
    }

    getApprovals = async (): Promise<IApprovalRequest[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getApprovals)
        const body = {}
        return await HttpClient.post<IApprovalRequest[]>(url, body, headers)
    }

    sendApprovalToReview = async (uuid: string, approvalGroupUUID?: string): Promise<IApprovalRequest> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.sendApprovalToReview)
        const body = { uuid, approvalGroupUUID }
        return await HttpClient.post<IApprovalRequest>(url, body, headers)
    }

    sendSettlementApprovalToReview = async (approvalEntityUUID: string, approvalGroupUUID?: string): Promise<IApprovalRequest> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.sendSettlementApprovalToReview)
        const body = { approvalEntityUUID, approvalGroupUUID }
        return await HttpClient.post<IApprovalRequest>(url, body, headers)
    }

    revertApprovalToDraft = async (uuid: string): Promise<IApprovalRequest> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.revertApprovalToDraft)
        const body = { uuid }
        return await HttpClient.post<IApprovalRequest>(url, body, headers)
    }

    approveRequest = async (uuid: string): Promise<IApprovalRequest> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.approveRequest)
        const body = { uuid }
        return await HttpClient.post<IApprovalRequest>(url, body, headers)
    }

    rejectApproval = async (uuid: string, rejectionDescription: string): Promise<IApprovalRequest> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.rejectApproval)
        const body = { uuid, rejectionDescription }
        return await HttpClient.post<IApprovalRequest>(url, body, headers)
    }

    archiveApproval = async (uuid: string): Promise<IApprovalRequest> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.archiveApproval)
        const body = { uuid }
        return await HttpClient.post<IApprovalRequest>(url, body, headers)
    }

    getContractVersions = async (contractAncestorKey?: string): Promise<ISimpleContractWithApproval[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractVersions)
        const body = { contractAncestorKey }
        const res = await HttpClient.post<ISimpleContractWithApproval[]>(url, body, headers)
        return res
    }

    addContractVersion = async (contractUUID: string, includeFiles: boolean): Promise<IContract> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.addContractVersion)
        const body = { contractUUID, includeFiles }
        console.log('[debug] ~ ApiClientClass ~ body.includeFiles:', body.includeFiles)
        console.log('[debug] ~ ApiClientClass ~ body.contractUUID:', body.contractUUID)
        return await HttpClient.post<IContract>(url, body, headers)
    }

    getEntityApproval = async (approvalEntityUUID: string): Promise<IApprovalRequest> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getEntityApproval)
        const body = { approvalEntityUUID }
        return await HttpClient.post<IApprovalRequest>(url, body, headers)
    }

    getUserApprovals = async (): Promise<IApprovalRequest[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getUserApprovals)
        const body = {}
        return await HttpClient.post<IApprovalRequest[]>(url, body, headers)
    }

    assignUsersToRole = async (roleUUID: string, userEmails: IUser['email'][]): Promise<boolean> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.assignUsersToRole)
        const body = { roleUUID, userEmails }
        return await HttpClient.post<boolean>(url, body, headers)
    }

    removeUsersFromRole = async (userEmails: IUser['email'][], roleUUID: string): Promise<boolean> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.removeUsersFromRole)
        const body = { userEmails, roleUUID }
        return await HttpClient.post<boolean>(url, body, headers)
    }

    refreshAllMaterializedViews = async (): Promise<IMaterializedViewRefreshResponse> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.nexus.refreshAllMaterializedViews)

        const body = {}

        return await HttpClient.post<IMaterializedViewRefreshResponse>(url, body, headers)
    }

    getContractFiles = async (contractUUID: string): Promise<IUploadedFile[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractFiles)
        const body = { contractUUID }

        return await HttpClient.post<IUploadedFile[]>(url, body, headers)
    }

    getSystemConfigsCollection = async (): Promise<ISystemConfigsCollection[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getSystemConfigsCollection)
        const body = {}

        return await HttpClient.post<ISystemConfigsCollection[]>(url, body, headers)
    }

    getEntitiesAudit = async (entityType: string, approvalEntityUUID: string, originalEntityUUID: string): Promise<any> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getEntitiesAudit)
        const body = { approvalEntityUUID, entityType, originalEntityUUID }
        return await HttpClient.post<any>(url, body, headers)
    }

    getPendingContracts = async (): Promise<IContract[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getPendingContracts)
        const body = {}
        const res = await HttpClient.post<IContract[]>(url, body, headers)
        return res
    }

    getUserInfo = async (uuid: string): Promise<Pick<IUser, 'name' | 'fullName' | 'image'>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getUserInfo)
        const body = { uuid }
        const res = await HttpClient.post<Pick<IUser, 'name' | 'fullName' | 'image'>>(url, body, headers)
        return res
    }

    getHeadContractNames = async (): Promise<Pick<IContract, 'uuid' | 'name' | 'id' | 'versioning'>[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getHeadContractNames)
        const body = {}
        const res = await HttpClient.post<Pick<IContract, 'uuid' | 'name' | 'id' | 'versioning'>[]>(url, body, headers)
        return res
    }

    getContractExpensesGroupedByProvider = async (contractId: number, startDate: string, endDate: string, limit?: number, offset?: number): Promise<IPaginationQueryResponse<IProviderExpense>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractExpensesGroupedByProvider)
        const body = {
            contractId,
            startDate,
            endDate,
            limit: (limit ? limit : null),
            order: (offset ? offset : null)
        }
        const res = await HttpClient.post<IPaginationQueryResponse<IProviderExpense>>(url, body, headers)
        return res
    }

    getContractTotalExpenseByPeriod = async (contractId: number, startDate: string, endDate: string): Promise<ITosExpense[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractTotalExpenseByPeriod)
        const body = { contractId, startDate, endDate }

        const res = await HttpClient.post<ITosExpense[]>(url, body, headers)
        return res
    }

    getContractCostUtilizationByPeriod = async (contractId: number, startDate: string, endDate: string, limit?: number, offset?: number, order?: { field: string, type: 'ASC' | 'DESC' }): Promise<IPaginationQueryResponse<ICostUtilization>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractCostUtilizationByPeriod)
        const body = {
            contractId,
            startDate,
            endDate,
            limit: (limit ? limit : null),
            order: (offset ? offset : null),
            ...(order && { order: { field: order.field, type: order.type } })
        }
        const res = await HttpClient.post<IPaginationQueryResponse<ICostUtilization>>(url, body, headers)
        return res
    }

    getContractKpiByPeriod = async (contractId: number, startDate: string, endDate: string): Promise<IKpiResponse> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractKpiByPeriod)
        const body = { contractId: contractId, startDate: startDate, endDate: endDate }
        const res = await HttpClient.post<IKpiResponse>(url, body, headers)
        return res
    }

    getContractRevenueExpenseTrendByPeriod = async (contractId: number, startDate: string, endDate: string): Promise<IChartData<IRevenueExpense>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractRevenueExpenseTrendByPeriod)
        const body = { contractId: contractId, startDate: startDate, endDate: endDate }
        const res = await HttpClient.post<IChartData<IRevenueExpense>>(url, body, headers)
        return res
    }

    getContractMarginExpenseByPeriod = async (contractId: number, startDate: string, endDate: string): Promise<IChartData<IMarginExpense>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractMarginExpenseByPeriod)
        const body = { contractId: contractId, startDate: startDate, endDate: endDate }
        const res = await HttpClient.post<IChartData<IMarginExpense>>(url, body, headers)
        return res
    }

    deleteSingleFile = async (fileKey: string, contractUUID: string): Promise<{
        success: boolean
        action: 'deleted' | 'unlinked'
    }> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.deleteSingleFile)
        const body = { fileKey, contractUUID }
        return await HttpClient.post<{ success: boolean, action: 'deleted' | 'unlinked' }>(url, body, headers)
    }

    getContractTotalExpensePMPMByPeriod = async (contractId: number, startDate: string, endDate: string): Promise<ITosExpense[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractTotalExpensePMPMByPeriod)
        const body = { contractId, startDate, endDate }

        const res = await HttpClient.post<ITosExpense[]>(url, body, headers)
        return res
    }

    getContractExpensesPMPMGroupedByProvider = async (contractId: number, startDate: string, endDate: string, limit?: number, offset?: number): Promise<IPaginationQueryResponse<IProviderExpense>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.os.getContractExpensesPMPMGroupedByProvider)
        const body = {
            contractId,
            startDate,
            endDate,
            limit: (limit ? limit : null),
            order: (offset ? offset : null)
        }
        const res = await HttpClient.post<IPaginationQueryResponse<IProviderExpense>>(url, body, headers)
        return res
    }
}

const ApiClient = new ApiClientClass()
export default ApiClient
